import type Dexie from 'dexie';
import { v001Initial } from './v001_initial';
import { v002HypothesisCreatedAt } from './v002_hypothesis_created_at';
import { v003MatchFormat } from './v003_match_format';
import { v004BDataModel } from './v004_b_data_model';
import { v005TeamProfile } from './v005_team_profile';

/**
 * NFR-7 — the product's forward-only migration chain, in order.
 *
 * Append new versions; **never reorder, never edit a migration that has shipped.** Dexie applies
 * every version between the one on the device and the newest declared here, so this list *is* the
 * upgrade path.
 *
 * Design §13.3: a migration that needs a recount sets a `reconcileNeeded` flag consumed by a
 * post-open pass in the app layer (which may use `domain/`); **reconciliation is not a migration
 * step.** That is what keeps "migrations never import `domain/`" true as the schema evolves — the
 * one internal contradiction the design's Phase 3 found in the migration story.
 *
 * Design §13.4: never roll back across a version bump. Forward-only migrations make that
 * data-destructive; the `VersionError` recovery screen (§13.5) is the safety net.
 */
const PRODUCT_MIGRATIONS: readonly ((db: Dexie) => void)[] = [
  v001Initial,
  v002HypothesisCreatedAt,
  v003MatchFormat,
  v004BDataModel,
  v005TeamProfile,
];

export function applyProductMigrations(db: Dexie): void {
  for (const migrate of PRODUCT_MIGRATIONS) migrate(db);
}

/**
 * The version a fresh install lands on.
 *
 * Derived from the chain length rather than declared, so appending a migration file cannot leave the
 * declared version behind. `config.PRODUCT_DB_VERSION` is asserted equal to it in `db.test.ts` —
 * that is the two-way check, in the same spirit as §1.3's constants diff.
 */
export const LATEST_PRODUCT_VERSION = PRODUCT_MIGRATIONS.length;

/**
 * Applies only the first `count` versions.
 *
 * Exists for the NFR-7 fixture test: it builds a database at **v001 exactly**, closes it, and
 * reopens it through the full chain — which is the only way to exercise Dexie's real upgrade path
 * rather than a fresh install that happens to end at the same schema.
 */
export function applyProductMigrationsUpTo(db: Dexie, count: number): void {
  for (const migrate of PRODUCT_MIGRATIONS.slice(0, count)) migrate(db);
}
